// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Button, { THEMES } from 'src/components/HTKit/Forms/Button';
import Icon from 'src/components/HTKit/Icon';
import ConfirmModal from 'src/components/HTKit/Modals/Confirm';
import SubNav from 'src/components/Layout/SubNav';
import ButtonFooter from 'src/components/ButtonFooter';
import PreQuestion from 'src/components/PreQuestion';
import { checkPartnerCart, filterRemoteQuestions, getItemDisplayPrice } from './utils';
import { OVERRIDE_BASE_PRICE_DISCLAIMER } from './constants';
import AddSkuHeader from './AddSkuHeader';
import AddSkuPageDisclaimer from './AddSkuPageDisclaimer';
import AddSkuPageBanner from './AddSkuPageBanner';
import AddTopSkuNotice from './AddTopSkuNotice';
import styles from './styles.scss';
import { useIsPasswordlessCheckoutEnabled } from '../Account/AccountPage/AccountPage.hooks';

class AddSkuPage extends React.Component {
  static propTypes = {
    addToCart: PropTypes.func.isRequired,
    confirmItem: PropTypes.func.isRequired,
    sku: PropTypes.object,
    cart: PropTypes.object,
    questions: PropTypes.object,
    page: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    isConfirmation: PropTypes.bool,
    inEditSkuMode: PropTypes.bool,
    itemTotalPrice: PropTypes.any,
    isMobileOrTablet: PropTypes.bool,
    isPasswordlessCheckoutEnabled: PropTypes.bool,
  };

  constructor(props, context) {
    super(props, context);
    this.elements = { removeItemsModal: null };
    this.state = {
      visible: false,
    };
  }

  componentWillUnmount() {
    document.body.style.overflow = 'visible';
  }

  onItemsRemoveConfirm = () => {
    this.props.addToCart();
  };

  // TODO have as standalone component one day...
  getButton = ({ isConfirmation, inEditSkuMode }) => {
    let title = 'Add to Cart';
    let action = this.addToCart;

    if (isConfirmation) {
      title = 'Next';
      action = this.updateItem;
    }
    if (inEditSkuMode) {
      title = 'Update';
      action = this.updateItem;
    }

    return (
      <Button mediumV2 theme={THEMES.V2PRIMARY} onClick={action}>
        <div className="flex alignItemsBaseline">
          <Icon name="cart-outlined" className="marginRight-tiny" />
          {title}
        </div>
      </Button>
    );
  };

  addToCart = (event) => {
    if (event) {
      event.preventDefault();
    }
    const { cart, sku } = this.props;
    const showItemRemovalDialog = checkPartnerCart(cart, sku).showDialog;
    if (showItemRemovalDialog) {
      this.elements.removeItemsModal.open();
    } else {
      this.props.addToCart({ isPasswordless: this.props.isPasswordlessCheckoutEnabled });
    }
  };

  updateItem = (event) => {
    event.preventDefault();
    this.props.confirmItem({ isPasswordless: this.props.isPasswordlessCheckoutEnabled });
  };

  render() {
    const {
      page,
      sku,
      cart,
      item,
      isConfirmation,
      itemTotalPrice,
      isMobileOrTablet,
      inEditSkuMode,
    } = this.props;
    const { visible } = this.state;
    const questions = filterRemoteQuestions({ questions: this.props.questions, sku, item, cart });
    const itemDialogOptions = checkPartnerCart(cart, sku);
    const showDiscountAppliedLater = sku.get('showDiscountAppliedLater');
    const itemDisplayPrice = getItemDisplayPrice({ item, total: itemTotalPrice });
    const banner = sku.getIn(['skuMessagings', 'banner']);
    const disclaimer = sku.getIn(['skuMessagings', 'disclaimer']);
    const bullets = sku.getIn(['skuMessagings', 'bullets']);
    const hasBullets = !!bullets && bullets.size > 0;
    const nextButton = this.getButton({
      isConfirmation,
      inEditSkuMode,
    });

    const outerSkuWrapperStyle = cn('l-content-section', styles.outerSkuWrapper, {
      [styles.withBullets]: hasBullets && isMobileOrTablet,
    });
    const questionsColumnStyles = cn({
      [styles.questionColumn]: !hasBullets,
      [styles.questionColumnWithBullets]: hasBullets,
    });

    return (
      <div>
        <SubNav innerClasses={styles.subNavInnerClass}>
          <AddSkuHeader
            name={sku.get('name')}
            itemDisplayPrice={itemDisplayPrice}
            showDiscountAppliedLater={showDiscountAppliedLater}
          />
          <AddTopSkuNotice sku={sku} bullets={bullets} visible={visible} />
        </SubNav>

        <AddSkuPageBanner banner={banner} />

        <div className={outerSkuWrapperStyle}>
          <div className={questionsColumnStyles}>
            {itemDialogOptions.showDialog && (
              <ConfirmModal
                ref={(el) => {
                  this.elements.removeItemsModal = el;
                }}
                titleStyles={styles.confirmationModalTitle}
                onConfirm={this.onItemsRemoveConfirm}
                cancelButtonTitle="NO"
                confirmButtonTitle="YES"
                title={itemDialogOptions.title}
                description={itemDialogOptions.description}
              />
            )}
            <div className="form form-container -center">
              {questions.map((q, index) => (
                <div key={index} className={styles.formElementWrapper}>
                  <PreQuestion
                    question={q}
                    sku={this.props.sku}
                    error={page.getIn(['errors', q.get('id').toString()])}
                  />
                </div>
              ))}
              <div className={styles.buttonWrapper}>
                {nextButton}
                {showDiscountAppliedLater && (
                  <p
                    className={`p1 text-align-center ${styles.showDiscountAppliedLaterDisclaimer}`}
                  >
                    {OVERRIDE_BASE_PRICE_DISCLAIMER}
                  </p>
                )}
              </div>
              {showDiscountAppliedLater && (
                <p
                  className={`p1 text-align-center ${styles.showDiscountAppliedLaterDisclaimerMobile}`}
                >
                  {OVERRIDE_BASE_PRICE_DISCLAIMER}
                </p>
              )}
            </div>

            <AddSkuPageDisclaimer disclaimer={disclaimer} />
          </div>

          {bullets.size > 0 && (
            <div className={styles.skuBullets}>
              <h4>IMPORTANT:</h4>
              <ul>
                {bullets.map((bullet, index) => (
                  <li key={index}>
                    <p className="p1 n700">{bullet}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ButtonFooter noZIndex showTablet={false} showDesktop={false}>
            {nextButton}
          </ButtonFooter>
        </div>
      </div>
    );
  }
}

const AddSkuPageWrapper = (props) => {
  const isPasswordlessCheckoutEnabled = useIsPasswordlessCheckoutEnabled();
  return <AddSkuPage {...props} isPasswordlessCheckoutEnabled={isPasswordlessCheckoutEnabled} />;
};

export default AddSkuPageWrapper;
